import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, FileWarning, Search, Map, ShieldCheck, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api, fileUrl } from '../lib/api';
import { Loading } from '../components/Shared';

export default function HostedTracker() {
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/network/tracker/${slug}`).then((r) => setConfig(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Loading />;

  if (notFound || !config) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="hosted-tracker-inactive">
        <MapPin className="h-10 w-10 text-[#C9A84C] mx-auto" />
        <h1 className="font-serif font-black text-[#1a2744] text-2xl mt-4">This tracker is not active yet</h1>
        <p className="text-slate-600 mt-2 leading-relaxed">Hosted trackers go live only for candidates who have joined the Public Issue Tracker App (PITA) Network and been approved. In the meantime, you can use the Ward 8 tracker or learn how to bring PITA to your ward.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild className="h-12 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press justify-center" data-testid="inactive-ward8-btn">
            <Link to="/issue-tracker">Ward 8 Issue Tracker App (PITA)</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-[#1a2744] text-[#1a2744] font-bold btn-press justify-center" data-testid="inactive-network-btn">
            <Link to="/tracker-network">Join the Network</Link>
          </Button>
        </div>
      </div>
    );
  }

  const reportLink = `/issue-tracker/report?tracker=${config.slug}`;

  return (
    <div data-testid="hosted-tracker-page">
      {/* Branded header */}
      <div className="bg-[#1a2744] relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)', backgroundSize: '56px 56px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex items-center gap-4">
            {config.photo_url && <img src={fileUrl(config.photo_url)} alt={config.name} className="h-16 w-16 rounded-2xl object-cover ring-2 ring-white/25" />}
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-[#C9A84C]" data-testid="hosted-tracker-kicker">{config.name}</p>
              <h1 className="text-white font-serif font-black text-2xl sm:text-4xl leading-tight" data-testid="hosted-tracker-title">{config.title}</h1>
              <p className="text-[#C9A84C] font-bold mt-1" data-testid="hosted-tracker-tagline">{config.tagline}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button asChild className="h-12 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press justify-center" data-testid="hosted-report-btn">
              <Link to={reportLink}><FileWarning className="h-4 w-4 mr-1.5" /> Report an Issue</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-center" data-testid="hosted-track-btn">
              <Link to="/issue-tracker/track"><Search className="h-4 w-4 mr-1.5" /> Track My Issue</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-center" data-testid="hosted-map-btn">
              <Link to="/map"><Map className="h-4 w-4 mr-1.5" /> Public Issue Map</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Card>
          <CardContent className="p-5 flex gap-3">
            <ShieldCheck className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed" data-testid="hosted-privacy-note">
              Reports are privacy-safe: your name, contact details, and exact address are never shown publicly. Public listings happen only with your consent. Every report receives a tracking number you can follow any time.
            </p>
          </CardContent>
        </Card>
        {!config.home && config.website_url && (
          <a href={config.website_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#1a2744] underline underline-offset-2" data-testid="hosted-candidate-site">
            <ExternalLink className="h-4 w-4" /> Visit {config.name}'s campaign site
          </a>
        )}
        <p className="mt-6 text-xs text-slate-500" data-testid="hosted-powered-by">
          Powered by the <Link to="/tracker-network" className="underline underline-offset-2 font-bold">PITA Network</Link>, built by Steve Lesperance, Ward 8. <Badge variant="outline" className="ml-1 text-[10px] border-slate-300">Not an official City service</Badge>
        </p>
      </div>
    </div>
  );
}
